import React from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useGetAccount } from "api/account/service";
import {
  THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT_ACTIVE,
  THEME2_TOOLBAR_TRIGGER_HOVER_BG_DARK_SURFACE,
  THEME2_TOOLBAR_TRIGGER_HOVER_BG_LIGHT_SURFACE,
  THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE,
  THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE,
} from "./adminNavbarTheme2Appearance";

const FutureFeature = ({
  lightNavbarSurface = false,
  toolbarIconFontSize,
  toolbarIconColor,
  toolbarIconColorActive,
  ...props
}) => {
  const navigate = useNavigate();
  const [classes, setClasses] = React.useState("dropdown");
  const menuRef = React.useRef(null);
  const { data: account } = useGetAccount();
  const [triggerHovered, setTriggerHovered] = React.useState(false);

  const handleClick = () => {
    if (classes === "dropdown") {
      setClasses("dropdown show");
    } else {
      setClasses("dropdown");
    }
  };

  const handleItemClick = (url) => {
    if (url) {
      navigate(url);
    }
    setClasses("dropdown");
  };

  React.useEffect(() => {
    if (classes === "dropdown show") {
      const handleOutsideClick = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setClasses("dropdown");
        }
      };
      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
  }, [classes]);

  const categories = [
    { id: 'inventory', label: 'Inventory', color: '#1f8ef1' },
    { id: 'deals', label: 'Deals', color: '#ff8d72' },
    { id: 'accounts', label: 'Accounts', color: '#d048b6' },
    { id: 'customers', label: 'Customers', color: '#00f2c3' }
  ];

  const fontStyle = {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'
  };

  const panel = lightNavbarSurface
    ? {
        panelBg: "#ffffff",
        columnBorder: "#e9ecef",
        headerBg: "#f6f9fc",
        headerBorder: "#e9ecef",
        headerText: "#525f7f",
        itemText: "#8E929D",
        emptyText: "rgba(0, 0, 0, 0.45)",
        boxShadow: "0 15px 35px rgba(50,50,93,.1), 0 5px 15px rgba(0,0,0,.07)",
      }
    : {
        panelBg: "#272c34",
        columnBorder: "rgba(255, 255, 255, 0.12)",
        headerBg: "#1f242d",
        headerBorder: "rgba(255, 255, 255, 0.1)",
        headerText: "#ffffff",
        itemText: "#8E929D",
        emptyText: "rgba(255, 255, 255, 0.6)",
        boxShadow: "0 16px 40px rgba(0, 0, 0, 0.45)",
      };

  const darkRowHoverBg = "rgba(255, 255, 255, 0.08)";

  const hoverBg = lightNavbarSurface
    ? THEME2_TOOLBAR_TRIGGER_HOVER_BG_LIGHT_SURFACE
    : THEME2_TOOLBAR_TRIGGER_HOVER_BG_DARK_SURFACE;
  const triggerBg = triggerHovered
    ? hoverBg
    : lightNavbarSurface
      ? classes.includes("show")
        ? "rgba(0, 0, 0, 0.08)"
        : "transparent"
      : classes.includes("show")
        ? "rgba(255, 255, 255, 0.15)"
        : "transparent";
  const triggerIconColor = (() => {
    if (toolbarIconColor != null) {
      if (classes.includes("show")) return toolbarIconColorActive ?? "#1d8cf8";
      if (triggerHovered && lightNavbarSurface) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE;
      if (triggerHovered) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE;
      return toolbarIconColor;
    }
    if (classes.includes("show")) return "#1d8cf8";
    if (triggerHovered && lightNavbarSurface) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE;
    if (triggerHovered) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE;
    return lightNavbarSurface ? "rgba(0, 0, 0, 0.55)" : "white";
  })();

  return (
    <div className="settings" style={{}} ref={menuRef}>
      <div className={classes} >
        <a
          href="#pablo"
          onClick={(e) => {
            e.preventDefault();
            handleClick();
          }}
          onMouseEnter={() => setTriggerHovered(true)}
          onMouseLeave={() => setTriggerHovered(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            backgroundColor: triggerBg,
            transition: 'all 0.2s ease'
          }}
        >
          <i
            className="tim-icons icon-refresh-02"
            style={{
              fontSize: toolbarIconFontSize ?? "1.5rem",
              color: triggerIconColor,
              ...(toolbarIconColor != null &&
              !lightNavbarSurface &&
              classes.includes("show")
                ? { fontWeight: THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT_ACTIVE }
                : {}),
            }}
          />
        </a>
        <ul
          className={classNames("dropdown-menu", "show", {
            "dtiq-future-feature-panel--light": lightNavbarSurface,
          })}
          style={{
            position: "absolute",
            right: "0",
            padding: "0",
            zIndex: 9999,
            marginTop: "100px",
            minWidth: "850px",
            maxHeight: "500px",
            background: panel.panelBg,
            display: "flex",
            flexDirection: "row",
            boxShadow: panel.boxShadow,
            border: "none",
            overflow: "hidden",
            ...fontStyle
          }}
        >
          {categories.map((cat, idx) => {
            const items = account?.history?.[cat.id] || [];
            return (
              <div
                key={cat.id}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: idx < categories.length - 1 ? `1px solid ${panel.columnBorder}` : "none",
                  minWidth: '210px'
                }}
              >
                {/* Category Header */}
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: panel.headerBg,
                  borderBottom: `1px solid ${panel.headerBorder}`,
                  textAlign: 'center'
                }}>
                  <h6 style={{
                    margin: 0,
                    fontWeight: '700',
                    color: panel.headerText,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    ...fontStyle
                  }}>
                    {cat.label}
                  </h6>
                </div>

                {/* Items List */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '5px 0' }}>
                  {items.length === 0 ? (
                    <div style={{ padding: '15px', textAlign: 'center', color: panel.emptyText, ...fontStyle }}>
                      <small style={{ fontSize: '11px' }}>No items</small>
                    </div>
                  ) : (
                    items.map((item, index) => (
                      <div
                        key={`${item.id}-${index}`}
                        className="dtiq-future-feature-history-row"
                        style={{
                          padding: '8px 15px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s, color 0.2s',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onClick={() => handleItemClick(item.url)}
                        onMouseEnter={
                          lightNavbarSurface
                            ? undefined
                            : (e) => {
                                e.currentTarget.style.backgroundColor = darkRowHoverBg;
                              }
                        }
                        onMouseLeave={
                          lightNavbarSurface
                            ? undefined
                            : (e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                              }
                        }
                      >
                        <div
                          className="dtiq-future-feature-history-label"
                          style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: panel.itemText,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...fontStyle,
                          }}
                        >
                          {item.label}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default FutureFeature;
